import React, { useState } from "react";
import { formatGoodsAndServices } from "../../lib/format";

const CLAMP = {
  1: "line-clamp-1",
  2: "line-clamp-2",
  3: "line-clamp-3",
  4: "line-clamp-4",
};

/**
 * Goods & services for one row, in the same three places it appears (grid
 * card, list row, table cell).
 *
 * - While the full record is still loading: a skeleton, so the row doesn't
 *   look like the register has nothing to say.
 * - Once loaded: a clamped one-paragraph summary ("IC 009: …  IC 042: …").
 *   When `expandable`, "Show more" opens it as one line per class.
 * - Nothing published: a muted placeholder.
 */
export default function GoodsAndServices({
  items,
  loading = false,
  lines = 3,
  expandable = false,
  emptyLabel = "Not published",
  className = "",
}) {
  const [open, setOpen] = useState(false);
  const summary = formatGoodsAndServices(items);
  const entries = (items || []).filter((g) => g.description);

  if (!summary) {
    if (loading) {
      return (
        <div
          className={`space-y-1.5 ${className}`}
          aria-label="Loading goods and services"
          role="status"
        >
          <div className="h-3 w-full animate-pulse rounded bg-ink-100" />
          <div className="h-3 w-4/5 animate-pulse rounded bg-ink-100" />
        </div>
      );
    }
    return <span className={`text-ink-300 ${className}`}>{emptyLabel}</span>;
  }

  // Roughly how many characters fit before the clamp starts to bite.
  const canExpand = expandable && summary.length > lines * 60;

  return (
    <div className={className}>
      {open ? (
        <ul className="space-y-1">
          {entries.map((g, i) => (
            <li key={`${g.classNumber}-${i}`}>
              {g.classNumber ? (
                <span className="font-semibold">IC {g.classNumber}: </span>
              ) : null}
              {g.description}
            </li>
          ))}
        </ul>
      ) : (
        <p className={CLAMP[lines] || CLAMP[3]} title={summary}>
          {summary}
        </p>
      )}

      {canExpand && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setOpen((v) => !v);
          }}
          className="mt-1 text-micro font-semibold text-[#8f5eeb]-600 hover:text-[#8f5eeb]-500"
        >
          {open ? "Show less" : "Show more"}
        </button>
      )}
    </div>
  );
}
